import FilterIcon from '@mui/icons-material/TuneOutlined';
import { Paper, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GeneralLoading from '@/components/GeneralLoading';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DetailsModal from '@/components/Modals/DetailsModal/DetailsModal';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import SearchInput from '@/components/SearchInput';
import useResponsive from '@/hooks/useResponsive';

import Charts from '../Charts/Charts';
import Labels from '../Charts/Labels';
import Filters from '../Filters/Filters';
import { shipColumns } from './shipments-columns';
import useMyShipments from './useMyShipments';

const MyShipmentsSection = () => {
  const hook = useMyShipments();
  const theme = useTheme();
  const isMobile = useResponsive('down', 'sm');
  const { t } = useTranslation('myShipments');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <Grid container spacing={2} justifyContent="center" alignItems="center" size={{ xl: 12 }}>
        {hook.statistics.data &&
          hook.statistics.data.map((item) => (
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              size={{ xxs: 12, lg: 8, xl: 8, xxl: 4 }}
            >
              <Paper
                elevation={3}
                sx={{
                  minHeight: '13.3125rem',
                  width: '100%',
                }}
              >
                {hook.statistics.loading ? (
                  <GeneralLoading size={40} withDots={false} />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xxs: 'center', lg: 'start' },
                      justifyContent: { xxs: 'center', lg: 'center' },
                      padding: ` ${theme.spacing(4)} ${theme.spacing(2)} ${theme.spacing(2)} `,
                      flexDirection: isMobile ? 'column' : 'row',
                    }}
                  >
                    <Charts data={item} />
                    <Labels item={item} />
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
      </Grid>

      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: `${theme.spacing(4)} 0 0`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          height="auto"
          sx={{
            padding: `0 ${theme.spacing(4)}`,
            flexDirection: { xxs: 'column', xl: 'row' },
            gap: { xxs: '1rem', xl: 0 },
            alignItems: 'center',
          }}
        >
          <SearchInput
            buttonText={t('BUTTONS.SEARCH_BOX.LABEL')}
            placeholder={t('BUTTONS.SEARCH_BOX.PLACEHOLDER')}
            value={hook.search}
            error={!!hook.searchError}
            helperText={hook.searchError}
            handleKeyPress={hook.handleKeyPress}
            onChange={hook.onChgSearch}
            onClick={hook.onSearch}
            sx={{ width: '100%' }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', xs: 'row' },
              gap: '1rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button variant="outlined" onClick={hook.clearFilters} size="small">
              {t('BUTTONS.CLEAR')}
            </Button>
            <Box
              display="flex"
              gap="0.5rem"
              alignItems="center"
              justifyContent="center"
              onClick={hook.handleOpenDrawer}
              sx={{
                cursor: 'pointer',
                ':hover': { backgroundColor: theme.palette.primary.lighter },
                padding: '0.5rem',
                borderRadius: '1rem',
              }}
            >
              <FilterIcon color="primary" />

              <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                {t('BUTTONS.FILTERS')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box width="100%" mt={2}>
          {hook.trackingIds.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                backgroundColor: 'rgb(248, 246, 244)',
                padding: '1rem 1rem 1rem 2%',
              }}
            >
              <Typography width="max-content">
                {t('TABLE.DOWNLOAD')}
                <strong>
                  {hook.trackingIds.length}
                  {`${t('TABLE.ORDER')}` +
                    (hook.trackingIds.length === 1
                      ? t('TABLE.SELECT_ONE')
                      : t('TABLE.SELECT_MULTIPLE'))}
                </strong>
                {t('TABLE.MAX_SELECT')}
              </Typography>
              <Button variant="contained" onClick={() => hook.handleOpenDownloadModal()}>
                {t('BUTTONS.DOWNLOAD')}
              </Button>
            </Box>
          )}
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={shipColumns(hook.handleOpenDownloadModal)}
                rows={hook.shipments.data || []}
                localeText={{
                  noRowsLabel: hook.hasActiveFilters()
                    ? 'No encontramos ninguna orden que coincida con tu búsqueda.'
                    : 'No hay envíos disponibles',
                }}
                rowCount={
                  hook.shipments.totalPages
                    ? hook.shipments.totalPages * (hook.pagination.filters.page_size as number)
                    : 0
                }
                loading={hook.shipments.loading}
                checkboxSelection={true}
                disableRowSelectionOnClick={true}
                onCellClick={(params) => {
                  if (!['actions', '__check__'].includes(params.colDef.field)) {
                    hook.fetchShipmentsByTrackingId(params.row.trackingId);
                  }
                }}
                getRowId={(row) => row.trackingId}
                hideFooterSelectedRowCount={true}
                paginationModel={{
                  page: hook.pagination.filters.page as number,
                  pageSize: hook.pagination.filters.page_size as number,
                }}
                disableColumnMenu={true}
                paginationMode="server"
                onPaginationModelChange={hook.pagination.onPaginationModelChange}
                isRowSelectable={(params) =>
                  !(hook.trackingIds.length + 1 > 10) ||
                  hook.trackingIds.includes(params.row.trackingId)
                }
                rowSelectionModel={{ ids: new Set(hook.trackingIds), type: 'include' }}
                onRowSelectionModelChange={(selectionModel) => {
                  const selectedArray = Array.from(selectionModel.ids) as string[];
                  if (selectedArray.length > 10) {
                    const limitedSelection = selectedArray.slice(0, 10);
                    hook.selectRows(limitedSelection);
                  } else {
                    hook.selectRows(selectedArray);
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
      <GenericDrawer
        open={hook.openDrawer}
        onClose={hook.handleCloseDrawer}
        title={t('FILTER_SECTION.TITLE')}
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hook.filters}
          onChange={hook.onChangeFilter}
          clearFilters={hook.clearFilters}
          applyFilters={hook.applyFilters}
        />
      </GenericDrawer>
      <DownloadModal
        isOpen={hook.downloadModal}
        onClose={hook.handleCloseDownloadModal}
        downloadLabels={hook.downloadLabels}
        trackingIds={hook.trackingIds}
      />
      <DetailsModal
        isOpen={hook.detailModal}
        onClose={hook.handleCloseDetailModal}
        orderDetails={hook.shipmentDetails.data}
        map={true}
        cancel={true}
      />
    </Box>
  );
};

export default MyShipmentsSection;
